import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';

import { IContentfulImage } from '../../../interfaces/contentful-image.interface';
import { IMeetingPlaceLinkToAnyPage } from '../../../queries/fragments/meetingPlaceLinkToAnyPage';
import LinkWrapper from '../../ui/link-wrapper/link-wrapper';

type TextAndImageCardColor = 'Blue' | 'Green' | 'Grey' | 'Red';

export interface TextAndImageCardBlockProps {
  title: string;
  cardText: string;
  cardLink?: IMeetingPlaceLinkToAnyPage;
  cardImage: IContentfulImage;
  cardTextOverlay: boolean;
  cardColor: TextAndImageCardColor;
  externalLink?: string;
  isMobileMenuCard?: boolean;
}

const TextAndImageCardBlock = ({
  title,
  cardText,
  cardLink,
  cardImage,
  cardTextOverlay,
  cardColor,
  externalLink,
  isMobileMenuCard = false,
}: TextAndImageCardBlockProps) => {
  const commonBackgroundColorClasses =
    'relative bg-cover aspect-1/0.7 transition-all duration-200 ease-in-out lg:hover:brightness-90';
  const cardBackgroundColorClass = (color: TextAndImageCardColor) => {
    switch (color) {
      case 'Blue':
        return `${commonBackgroundColorClasses} bg-brand-blue-1 w-full text-brand-blue-3 bg-dynamicStrokeBlue`;
      case 'Grey':
        return `${commonBackgroundColorClasses} bg-background-primary-light w-full text-brand-grey-4 bg-dynamicStrokeGreyDark`;
      case 'Green':
        return `${commonBackgroundColorClasses} bg-brand-green-1 w-full text-brand-green-3 bg-dynamicStrokeGreen`;
      case 'Red':
        return `${commonBackgroundColorClasses} bg-brand-red-1 w-full text-brand-red-3 bg-dynamicStrokeRed`;
      default:
        return '';
    }
  };

  const linkCss = `group relative mb-5 inline-block transform-gpu break-inside-avoid overflow-hidden rounded-xl lg:mb-5 ${
    cardBackgroundColorClass(cardColor)
      ? cardBackgroundColorClass(cardColor)
      : 'absolute inset-0 transition-all duration-200 ease-in-out lg:group-hover:bg-transparency-backdrop text-brand-white'
  }`;

  const text = (
    <div
      className={` ${
        cardTextOverlay
          ? 'absolute inset-0 bg-gradient-to-b from-transparent to-transparency-gradient-to'
          : 'absolute inset-0 '
      }
${
  cardImage
    ? 'transition-all duration-200 ease-in-out lg:group-hover:bg-transparency-backdrop'
    : ''
}`}
    >
      <div
        className={`absolute bottom-0 left-0 right-0 transform-gpu px-5 ${
          isMobileMenuCard ? 'pb-5 ' : 'pb-10 '
        } ${
          isMobileMenuCard && !cardImage ? 'top-0 flex items-center' : ' '
        }  pt-5`}
      >
        <h2
          className={`font-title ${
            isMobileMenuCard ? 'text-headingxs ' : 'text-headingm '
          } transition-all duration-200 ease-in-out lg:text-headingm ${
            cardImage && 'lg:group-hover:text-brand-yellow-1'
          }`}
        >
          {title}
        </h2>
        <p
          className={`text-bodym ${
            (!cardImage || isMobileMenuCard) &&
            'hidden max-h-0 overflow-hidden opacity-0 transition-all duration-200 ease-in-out lg:block lg:group-hover:max-h-20 lg:group-hover:opacity-100'
          }`}
        >
          {cardText}
        </p>
      </div>
    </div>
  );

  const image = cardImage ? (
    <GatsbyImage
      alt={cardImage?.description ? cardImage.description : ''}
      image={cardImage?.gatsbyImageData}
      className="aspect-0.75/1 lg:transition-all lg:duration-200 lg:ease-in-out lg:group-hover:scale-105"
      imgClassName="object-cover block w-full"
    />
  ) : null;

  const Links =
    cardLink && !externalLink ? (
      <LinkWrapper data={cardLink} className={linkCss}>
        {image}
        {text}
      </LinkWrapper>
    ) : (
      !cardLink &&
      externalLink && (
        <a
          target={'_blank'}
          rel={'noopener noreferrer'}
          href={externalLink}
          className={linkCss}
        >
          {cardImage ? image : null}
          {text}
        </a>
      )
    );

  return <>{Links}</>;
};

export default TextAndImageCardBlock;

export interface ITextAndImageCardBlock {
  blocktype: string;
  id: string;
  title: string;
  cardText: string;
  cardLink?: IMeetingPlaceLinkToAnyPage;
  cardImage: IContentfulImage;
  cardTextOverLay: boolean;
  cardColor: TextAndImageCardColor;
  externalLink?: string;
}

export const query = graphql`
  fragment TextAndImageCardBlock on ContentfulBlockTextAndImageCard {
    blocktype: __typename
    id
    title
    cardText
    cardLink {
      ...MeetingPlaceLinkToAnyPage
    }
    externalLink
    cardImage {
      description
      gatsbyImageData(
        width: 575
        height: 766
        layout: CONSTRAINED
        aspectRatio: 0.75
        formats: AVIF
      )
    }
    cardTextOverlay
    cardColor
  }
`;
